import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import {
  LogIn,
  LogOut,
  RefreshCw,
  Phone,
  Mail,
  Users2,
  Inbox,
  CheckCircle2,
  AlertCircle,
  Plus,
  ChevronDown,
  X,
  Calculator,
  TrendingUp,
  Activity,
  IndianRupee,
  Info,
  Landmark,
  Lock,
  Eye,
  EyeOff,
  Save,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { Partner, partnerApi, partnerTokenStorage } from "@/lib/api";
import DashboardBrandStrip from "@/components/DashboardBrandStrip";
import LanguageSelector from "@/components/LanguageSelector";
import { useI18n } from "@/lib/i18n";

const PARTNER_TOKEN_KEY = "partner_token";
const PARTNER_INFO_KEY = "partner_info";

/* ─── LOGIN ─────────────────────────────────────────────────────────── */

export function PartnerLogin() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (localStorage.getItem(PARTNER_TOKEN_KEY)) {
    return <Navigate to="/partner/dashboard" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await Partner.login(form);
      partnerTokenStorage.set(data.token);
      localStorage.setItem(PARTNER_INFO_KEY, JSON.stringify(data.partner));
      navigate("/partner/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="partner-login-page"
      className="portal-shell min-h-screen bg-[#04102B] text-white flex items-start md:items-center justify-center px-4 sm:px-5 py-8 md:py-10 relative"
    >
      <div className="absolute top-4 right-4 sm:top-5 sm:right-6 z-10">
        <LanguageSelector testId="partner-login-lang" />
      </div>
      <div className="w-full max-w-md mt-14 md:mt-0">
        <Link to="/" className="block text-center mb-8">
          <div className="font-display text-3xl tracking-tight">SAHYOG FINANCE</div>
          <div className="text-[#D4AF37] text-xs tracking-[0.22em] uppercase mt-1">
            Partner Portal · Login
          </div>
        </Link>
        <form
          data-testid="partner-login-form"
          onSubmit={submit}
          className="bg-[#0A1F44] rounded-3xl p-7 border border-[#D4AF37]/30"
        >
          <h1 className="font-display text-2xl">{t("login.title")}</h1>
          <p className="text-white/55 text-xs mt-1">
            {t("login.subtitle.partner")}
          </p>

          <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-6 mb-2">
            {t("login.field.identifier")} <span className="text-[#D4AF37]">*</span>
          </label>
          <input
            data-testid="partner-login-id"
            value={form.identifier}
            onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))}
            required
            className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]"
          />

          <label className="block text-[11px] uppercase tracking-wider text-white/55 mt-4 mb-2">
            {t("login.field.password")} <span className="text-[#D4AF37]">*</span>
          </label>
          <input
            data-testid="partner-login-pass"
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            required
            className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]"
          />

          {error && (
            <div data-testid="partner-login-error" className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="partner-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold disabled:opacity-70"
          >
            <LogIn size={16} /> {loading ? t("login.submitting") : t("login.submit")}
          </button>

          <div className="mt-4 text-center">
            <Link
              to="/partner/forgot-password"
              data-testid="partner-forgot-link"
              className="text-xs text-[#F0D788] hover:underline"
            >
              {t("login.forgot")}
            </Link>
          </div>

          <div className="mt-5 text-xs text-white/55 text-center">
            Don&apos;t have an account?{" "}
            <Link to="/apply/dsa" className="text-[#F0D788] underline">
              Apply as a partner
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── DASHBOARD ─────────────────────────────────────────────────────── */

const ROLE_LABEL = {
  dsa: "DSA / Associate",
  advisor: "Advisor",
  agency: "Agency Partner",
  associate: "Associate Partner",
  branch: "Branch",
};

const STATUS_OPTS = [
  "new",
  "contacted",
  "qualified",
  "pending",
  "approved",
  "rejected",
  "disbursed",
  "closed",
];

const STATUS_STYLE = {
  new: "bg-blue-500/15 text-blue-200 border-blue-500/30",
  contacted: "bg-indigo-500/15 text-indigo-200 border-indigo-500/30",
  qualified: "bg-cyan-500/15 text-cyan-200 border-cyan-500/30",
  pending: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  disbursed: "bg-emerald-600/25 text-emerald-100 border-emerald-500/50",
  rejected: "bg-rose-500/15 text-rose-200 border-rose-500/30",
  closed: "bg-white/10 text-white/60 border-white/20",
};

const INTEREST_OPTS = [
  "All Loan Types",
  "Personal Loan",
  "Business Loan",
  "Home Loan",
  "Mortgage Loan",
  "Agriculture Loan",
  "Vehicle Loan",
  "Gold Loan",
  "Other",
];

// How often the dashboard silently re-fetches leads (in ms) so the partner
// sees a "live" feed of activity without needing to hit Refresh.
const POLL_INTERVAL_MS = 30_000;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, closed_leads: 0, team_size: 0 });
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState("overview"); // overview | leads | team | calculator
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", email: "", interest: "Personal Loan", message: "" });
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [syncTick, setSyncTick] = useState(0); // re-render tick for the "Last synced Xs ago" label
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  // Lead filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterInterest, setFilterInterest] = useState("All Loan Types");
  const [filterQ, setFilterQ] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const hasToken = Boolean(localStorage.getItem(PARTNER_TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(PARTNER_TOKEN_KEY);
    localStorage.removeItem(PARTNER_INFO_KEY);
    navigate("/partner/login", { replace: true });
  }, [navigate]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [m, s, l, t] = await Promise.all([
        partnerApi.get("/partner/auth/me"),
        partnerApi.get("/partner/stats"),
        partnerApi.get("/partner/leads"),
        partnerApi.get("/partner/team"),
      ]);
      setPartner(m.data);
      setStats(s.data);
      setLeads(l.data.items || []);
      setTeam(t.data.items || []);
      setLastSyncedAt(new Date());
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        logout();
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);

  // Auto-refresh polling — silent background fetch to keep the feed live.
  useEffect(() => {
    if (!hasToken || !autoRefresh) return undefined;
    const id = setInterval(() => load({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasToken, autoRefresh, load]);

  // 1-second tick to update the "Last synced Xs ago" label smoothly.
  useEffect(() => {
    const id = setInterval(() => setSyncTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!hasToken) return <Navigate to="/partner/login" replace />;

  // ── Derived: filtered leads for the leads table ──────────────────
  const filteredLeads = leads.filter((l) => {
    if (filterStatus !== "all" && l.status !== filterStatus) return false;
    if (filterInterest !== "All Loan Types" && l.interest !== filterInterest) return false;
    if (filterQ) {
      const needle = filterQ.toLowerCase();
      const blob = `${l.name || ""} ${l.phone || ""} ${l.email || ""} ${l.id || ""}`.toLowerCase();
      if (!blob.includes(needle)) return false;
    }
    if (filterFrom) {
      if (new Date(l.created_at) < new Date(filterFrom)) return false;
    }
    if (filterTo) {
      // include end-of-day for the "to" filter
      const to = new Date(filterTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(l.created_at) > to) return false;
    }
    return true;
  });

  // Approved-family count for the summary card
  const approvedCount = leads.filter((l) =>
    ["approved", "disbursed", "qualified"].includes(l.status)
  ).length;

  const secondsSinceSync =
    lastSyncedAt ? Math.floor((Date.now() - lastSyncedAt.getTime()) / 1000) : null;
  // syncTick is intentionally read to force re-renders every second
  void syncTick;

  const updateLeadStatus = async (id, status) => {
    try {
      const { data } = await partnerApi.patch(`/partner/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      load();
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("lead update failed:", e);
    }
  };

  const createLead = async (e) => {
    e.preventDefault();
    try {
      const { data } = await partnerApi.post("/partner/leads", newLead);
      setLeads((p) => [data, ...p]);
      setNewLead({ name: "", phone: "", email: "", interest: "Personal Loan", message: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not add lead");
    }
  };

  return (
    <div data-testid="partner-dashboard" className="portal-shell min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/90 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[88px] flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg md:text-xl">
              {partner?.name || "Partner"}
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mt-0.5">
              {ROLE_LABEL[partner?.role] || partner?.role} · Code {partner?.referral_code}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Live sync indicator */}
            <div
              data-testid="partner-live-indicator"
              onClick={() => setAutoRefresh((v) => !v)}
              className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-emerald-400/25 text-[11px] cursor-pointer hover:border-emerald-400/50 transition"
              title={
                autoRefresh
                  ? "Auto-refreshing every 30s — click to pause"
                  : "Auto-refresh paused — click to resume"
              }
            >
              <span className="flex items-center gap-1.5">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    autoRefresh ? "bg-emerald-400 animate-pulse" : "bg-white/30"
                  }`}
                />
                <span className={autoRefresh ? "text-emerald-200" : "text-white/50"}>
                  {autoRefresh ? "LIVE" : "PAUSED"}
                </span>
              </span>
              <span className="text-white/40">·</span>
              <span className="text-white/55">
                {secondsSinceSync == null
                  ? "syncing…"
                  : secondsSinceSync < 5
                    ? "just now"
                    : `${secondsSinceSync}s ago`}
              </span>
            </div>
            <button
              data-testid="partner-refresh"
              onClick={() => load()}
              disabled={loading}
              className="text-white/80 hover:text-[#F0D788] inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-xs"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="partner-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-xs border border-white/10"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 space-y-6">
        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total Leads" value={stats.total_leads} testId="stat-total-leads" />
          <StatCard label="Approved" value={approvedCount} accent="text-emerald-300" testId="stat-approved" />
          <StatCard label="Est. Commission (Month)" value={`₹${Number(stats.estimated_commission_this_month || 0).toLocaleString('en-IN')}`} accent="text-emerald-300" testId="stat-commission-month" />
          <StatCard label="Est. Commission (Lifetime)" value={`₹${Number(stats.estimated_commission_all_time || 0).toLocaleString('en-IN')}`} accent="text-[#F0D788]" testId="stat-commission-total" />
        </section>

        {/* Tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="tab-overview"
            onClick={() => setTab("overview")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${tab === "overview" ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37] font-semibold" : "bg-white/[0.05] border-white/10"}`}
          >
            📊 Overview
          </button>
          <button
            data-testid="tab-leads"
            onClick={() => setTab("leads")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${tab === "leads" ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37] font-semibold" : "bg-white/[0.05] border-white/10"}`}
          >
            <Inbox size={14} /> My Leads ({leads.length})
          </button>
          <button
            data-testid="tab-team"
            onClick={() => setTab("team")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${tab === "team" ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37] font-semibold" : "bg-white/[0.05] border-white/10"}`}
          >
            <Users2 size={14} /> My Team ({team.length})
          </button>
          <button
            data-testid="tab-calculator"
            onClick={() => setTab("calculator")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${tab === "calculator" ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37] font-semibold" : "bg-white/[0.05] border-white/10"}`}
          >
            🧮 Commission Calculator
          </button>
          <button
            data-testid="tab-profile"
            onClick={() => setTab("profile")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${tab === "profile" ? "bg-[#D4AF37] text-[#0A1F44] border-[#D4AF37] font-semibold" : "bg-white/[0.05] border-white/10"}`}
          >
            🏦 Profile &amp; Bank
          </button>
          <a
            data-testid="tab-agreement"
            href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/partner/agreement/${partner?.id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border bg-white/[0.05] border-white/10 hover:border-[#D4AF37]/50 text-white/85"
            title="Download your Sahyog Partner Agreement PDF"
          >
            📄 Download Agreement
          </a>
          {tab === "leads" && (
            <button
              data-testid="add-lead"
              onClick={() => setShowAdd(true)}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full btn-gold text-sm font-semibold"
            >
              <Plus size={14} /> Add Lead
            </button>
          )}
        </div>

        {tab === "overview" && (
          <PartnerOverviewTab
            stats={stats}
            leads={leads}
            approvedCount={approvedCount}
            onOpenCalc={() => setTab("calculator")}
            onOpenLead={setSelectedLead}
            onGoToLeads={() => setTab("leads")}
          />
        )}

        {tab === "calculator" && (
          <CommissionCalculatorCard partnerRole={partner?.role || "dsa"} />
        )}

        {tab === "profile" && (
          <ProfileTab partner={partner} onPartnerUpdate={(p) => setPartner((prev) => ({ ...prev, ...p }))} />
        )}

        {tab === "leads" && (
          <>
            {/* Filter bar */}
            <section
              data-testid="leads-filter-bar"
              className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 p-4 flex flex-wrap items-center gap-2"
            >
              <div className="relative flex-1 min-w-[220px]">
                <input
                  data-testid="lead-filter-q"
                  value={filterQ}
                  onChange={(e) => setFilterQ(e.target.value)}
                  placeholder="Search name, phone, email, ID…"
                  aria-label="Search leads"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-4 pr-3 py-2 text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60"
                />
              </div>
              <select
                data-testid="lead-filter-status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                aria-label="Filter by status"
                className="bg-white/[0.04] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              >
                <option value="all" className="bg-[#04102B]">All statuses</option>
                {STATUS_OPTS.map((s) => (
                  <option key={s} value={s} className="bg-[#04102B]">
                    {s}
                  </option>
                ))}
              </select>
              <select
                data-testid="lead-filter-interest"
                value={filterInterest}
                onChange={(e) => setFilterInterest(e.target.value)}
                aria-label="Filter by loan type"
                className="bg-white/[0.04] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60"
              >
                {INTEREST_OPTS.map((o) => (
                  <option key={o} value={o} className="bg-[#04102B]">
                    {o}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/45">
                <span>From</span>
                <input
                  data-testid="lead-filter-from"
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs text-white/85"
                />
                <span>To</span>
                <input
                  data-testid="lead-filter-to"
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs text-white/85"
                />
              </div>
              {(filterQ || filterStatus !== "all" || filterInterest !== "All Loan Types" || filterFrom || filterTo) && (
                <button
                  data-testid="lead-filter-clear"
                  onClick={() => {
                    setFilterQ("");
                    setFilterStatus("all");
                    setFilterInterest("All Loan Types");
                    setFilterFrom("");
                    setFilterTo("");
                  }}
                  className="text-[11px] text-white/60 hover:text-[#F0D788] underline"
                >
                  Clear
                </button>
              )}
              <div className="ml-auto text-[11px] text-white/55">
                Showing <span className="text-white/90 font-semibold">{filteredLeads.length}</span> of{" "}
                <span className="text-white/70">{leads.length}</span>
              </div>
            </section>

            <section data-testid="leads-section" className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                    <tr>
                      <th className="text-left p-4">Lead</th>
                      <th className="text-left p-4">Contact</th>
                      <th className="text-left p-4">Loan Type</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-left p-4">Created</th>
                      <th className="text-right p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.length === 0 && !loading && (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-white/55">
                          {leads.length === 0
                            ? 'No leads yet. Click "Add Lead" to refer a customer.'
                            : "No leads match your filters."}
                        </td>
                      </tr>
                    )}
                    {filteredLeads.map((l) => (
                      <tr
                        key={l.id}
                        data-testid={`partner-lead-${l.id}`}
                        onClick={() => setSelectedLead(l)}
                        className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition"
                      >
                        <td className="p-4">
                          <div className="font-display text-base">{l.name}</div>
                          <div className="text-[10px] text-white/40 mt-1">ID · {l.id.slice(0, 8)}</div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1.5 text-white/85">
                            <Phone size={11} className="text-[#D4AF37]" /> {l.phone}
                          </div>
                          {l.email && (
                            <div className="flex items-center gap-1.5 text-white/55 text-xs mt-1">
                              <Mail size={10} /> {l.email}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-white/80">{l.interest}</td>
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <select
                            data-testid={`partner-lead-status-${l.id}`}
                            value={l.status}
                            onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                            className={`border rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}
                          >
                            {STATUS_OPTS.map((s) => (
                              <option key={s} value={s} className="bg-[#04102B] text-white normal-case">
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-4 text-white/65 whitespace-nowrap text-xs">
                          <div>{new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                          <div className="text-[10px] text-white/40">{timeAgo(l.created_at)}</div>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-[#F0D788] text-xs hover:underline">Open →</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {tab === "team" && (
          <section data-testid="team-section" className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                  <tr>
                    <th className="text-left p-4">Member</th>
                    <th className="text-left p-4">Role</th>
                    <th className="text-left p-4">Contact</th>
                    <th className="text-left p-4">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {team.length === 0 && !loading && (
                    <tr><td colSpan={4} className="p-6 text-center text-white/55">No downline yet. Share your referral code <span className="text-[#F0D788] font-mono">{partner?.referral_code}</span> with prospects.</td></tr>
                  )}
                  {team.map((t) => (
                    <tr key={t.id} data-testid={`partner-team-${t.id}`} className="border-t border-white/5">
                      <td className="p-4 font-display text-base">{t.name}</td>
                      <td className="p-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#F0D788] text-[10px] uppercase tracking-wider">
                          {ROLE_LABEL[t.role] || t.role}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1.5 text-white/85 text-xs"><Phone size={11} className="text-[#D4AF37]" /> {t.phone}</div>
                        {t.email && <div className="flex items-center gap-1.5 text-white/55 text-xs mt-1"><Mail size={10} /> {t.email}</div>}
                      </td>
                      <td className="p-4 text-white/65 whitespace-nowrap text-xs">
                        {new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "medium" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      {/* Lead detail drawer */}
      {selectedLead && (
        <LeadDetailDrawer
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={(newStatus) => {
            updateLeadStatus(selectedLead.id, newStatus);
            setSelectedLead((prev) => (prev ? { ...prev, status: newStatus } : prev));
          }}
        />
      )}

      {/* Add lead modal */}
      {showAdd && (
        <div data-testid="add-lead-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-5">
          <form
            onSubmit={createLead}
            className="bg-[#0A1F44] rounded-2xl p-7 border border-[#D4AF37]/40 w-full max-w-md"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl">Add Lead</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="text-white/55 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="mt-5 space-y-3">
              {[
                ["name", "Customer Name *", "text", true],
                ["phone", "Mobile *", "tel", true],
                ["email", "Email", "email", false],
              ].map(([k, lbl, t, req]) => (
                <div key={k}>
                  <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">{lbl}</label>
                  <input
                    type={t}
                    required={req}
                    data-testid={`new-lead-${k}`}
                    value={newLead[k]}
                    onChange={(e) => setNewLead((p) => ({ ...p, [k]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]"
                  />
                </div>
              ))}
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">Interest</label>
                <select
                  data-testid="new-lead-interest"
                  value={newLead.interest}
                  onChange={(e) => setNewLead((p) => ({ ...p, interest: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#04102B] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]"
                >
                  {["Personal Loan","Business Loan","Home Loan","Mortgage Loan","Agriculture Loan","Vehicle Loan","Gold Loan","Other"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">Message / Notes</label>
                <textarea
                  data-testid="new-lead-message"
                  rows={2}
                  value={newLead.message}
                  onChange={(e) => setNewLead((p) => ({ ...p, message: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] resize-none"
                />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-full text-sm bg-white/10 hover:bg-white/15">
                Cancel
              </button>
              <button type="submit" data-testid="new-lead-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full btn-gold text-sm font-semibold">
                <CheckCircle2 size={14} /> Add Lead
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function LeadDetailDrawer({ lead, onClose, onStatusChange }) {
  return (
    <div
      data-testid="lead-detail-drawer"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0A1F44] border-l border-[#D4AF37]/30 overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Lead Detail</div>
            <div className="font-display text-xl mt-1 text-white">{lead.name}</div>
            <div className="text-[11px] text-white/50 font-mono mt-1">ID · {lead.id}</div>
          </div>
          <button
            data-testid="lead-drawer-close"
            onClick={onClose}
            className="text-white/60 hover:text-white p-1"
            aria-label="Close lead details"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Current Status</div>
          <select
            data-testid="lead-drawer-status"
            value={lead.status}
            onChange={(e) => onStatusChange(e.target.value)}
            className={`w-full border rounded-full px-4 py-2 text-sm uppercase tracking-wider font-semibold ${STATUS_STYLE[lead.status] || STATUS_STYLE.new}`}
          >
            {STATUS_OPTS.map((s) => (
              <option key={s} value={s} className="bg-[#04102B] text-white normal-case">
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Contact</div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
            <div className="flex items-center gap-2 px-3 py-2.5">
              <Phone size={12} className="text-[#D4AF37]" />
              <a href={`tel:${lead.phone}`} className="text-white/90 text-sm hover:text-[#F0D788]">
                {lead.phone}
              </a>
            </div>
            {lead.email && (
              <div className="flex items-center gap-2 px-3 py-2.5">
                <Mail size={12} className="text-[#D4AF37]" />
                <a href={`mailto:${lead.email}`} className="text-white/90 text-sm hover:text-[#F0D788] break-all">
                  {lead.email}
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Loan / Interest</div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-sm text-white/90">
            {lead.interest || "—"}
          </div>
        </div>

        {lead.message && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Message / Notes</div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-sm text-white/80 leading-relaxed whitespace-pre-wrap">
              {lead.message}
            </div>
          </div>
        )}

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Timeline</div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 text-sm">
            <div className="flex items-start justify-between gap-3 px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wider text-white/50">Created</div>
              <div className="text-white/90 text-right">
                {new Date(lead.created_at).toLocaleString("en-IN")}
                <div className="text-[10px] text-white/45">{timeAgo(lead.created_at)}</div>
              </div>
            </div>
            {lead.source && (
              <div className="flex items-start justify-between gap-3 px-3 py-2.5">
                <div className="text-[11px] uppercase tracking-wider text-white/50">Source</div>
                <div className="text-white/90 text-right text-sm">{lead.source}</div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-6">
          <a
            href={`tel:${lead.phone}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 text-xs font-semibold"
          >
            <Phone size={12} /> Call now
          </a>
          {lead.email && (
            <a
              href={`mailto:${lead.email}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white/[0.05] border border-white/15 text-white/85 hover:bg-white/[0.1] text-xs font-semibold"
            >
              <Mail size={12} /> Email
            </a>
          )}
        </div>
      </div>
    </div>
  );
}


function StatCard({ label, value, accent = "text-white", testId }) {
  return (
    <div data-testid={testId} className="bg-[#0A1F44] rounded-xl border border-[#D4AF37]/20 p-4">
      <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}



/* ─── OVERVIEW TAB (dashboard) ───────────────────────────────────── */

function PartnerOverviewTab({ stats, leads = [], approvedCount = 0, onOpenCalc, onOpenLead, onGoToLeads }) {
  const perf = stats.monthly_performance || [];
  const maxLeads = Math.max(1, ...perf.map((b) => b.leads));
  const recent = leads.slice(0, 8);

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <DashboardBrandStrip />
      {/* Eligibility CTA (customer-facing tool DSAs can pitch) */}
      <section
        data-testid="partner-eligibility-cta"
        className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#0A1F44] via-[#0e2a6b] to-[#0A1F44] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#F0D788]">
            Customer tool
          </div>
          <div className="mt-1 font-display text-lg text-white">
            Loan Eligibility Check
          </div>
          <div className="text-xs text-white/60 mt-0.5">
            Share this link with prospects — assessment turnaround 5 to 6 working days for ₹1,500. Boosts your conversion pipeline.
          </div>
        </div>
        <a
          href="/eligibility"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold whitespace-nowrap"
        >
          Check My Loan Eligibility (₹1,500)
        </a>
      </section>

      {/* Secondary stat row — approved-family broken out */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="New" value={stats.new_leads} accent="text-blue-300" testId="stat-new-leads" />
        <StatCard label="Contacted" value={stats.contacted_leads || 0} accent="text-amber-300" testId="stat-contacted-leads" />
        <StatCard label="Approved / Disbursed" value={approvedCount} accent="text-emerald-300" testId="stat-approved-family" />
        <StatCard label="Closed" value={stats.closed_leads} accent="text-white/70" testId="stat-closed-leads" />
      </section>

      {/* Recent activity feed */}
      <section
        data-testid="recent-activity"
        className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <Activity size={16} className="text-[#F0D788]" />
          <h3 className="font-display text-lg">Recent Activity</h3>
          <span className="ml-auto text-[10px] text-white/45 uppercase tracking-wider">Latest {recent.length} leads</span>
        </div>
        {recent.length === 0 && (
          <div className="text-white/50 text-sm py-6 text-center">
            No leads yet. Add a lead or wait for referrals to come in — this feed refreshes automatically.
          </div>
        )}
        <ul className="divide-y divide-white/5">
          {recent.map((l) => (
            <li
              key={l.id}
              data-testid={`activity-${l.id}`}
              onClick={() => onOpenLead && onOpenLead(l)}
              className="flex items-center gap-3 py-3 px-2 -mx-2 rounded-lg hover:bg-white/[0.03] cursor-pointer transition"
            >
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/25 flex items-center justify-center shrink-0">
                <span className="text-[#F0D788] text-sm font-semibold">
                  {(l.name || "?").slice(0, 1).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-white truncate">{l.name}</span>
                  <span className="text-[10px] text-white/40">·</span>
                  <span className="text-xs text-white/70 truncate">{l.interest}</span>
                </div>
                <div className="text-[11px] text-white/45 mt-0.5">
                  {l.phone} · {timeAgo(l.created_at)}
                </div>
              </div>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}
              >
                {l.status}
              </span>
            </li>
          ))}
        </ul>
        {leads.length > recent.length && (
          <button
            onClick={onGoToLeads}
            className="mt-4 text-xs text-[#F0D788] hover:underline"
          >
            View all {leads.length} leads →
          </button>
        )}
      </section>

      {/* Monthly performance chart */}
      <section
        data-testid="monthly-chart"
        className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-[#F0D788]" />
          <h3 className="font-display text-lg">Monthly Performance</h3>
          <span className="ml-auto text-[11px] text-white/45">Last 6 months</span>
        </div>
        <div className="grid grid-cols-6 gap-3 items-end h-40">
          {perf.map((b) => {
            const h = Math.round((b.leads / maxLeads) * 100);
            const closedH = Math.round((b.closed / maxLeads) * 100);
            return (
              <div key={b.month} className="flex flex-col items-center gap-1.5 h-full">
                <div className="text-[10px] tabular-nums text-white/60">{b.leads}</div>
                <div className="flex-1 w-full flex items-end">
                  <div
                    className="relative w-full rounded-t-md bg-gradient-to-t from-[#D4AF37]/25 to-[#F0D788]/50 border-t border-[#D4AF37]/60 transition-all"
                    style={{ height: `${Math.max(h, 4)}%` }}
                    title={`${b.month} · ${b.leads} leads, ${b.closed} closed`}
                  >
                    {b.closed > 0 && (
                      <div
                        className="absolute bottom-0 left-0 right-0 rounded-t-md bg-emerald-500/70 border-t border-emerald-300"
                        style={{ height: `${Math.max((closedH / Math.max(h, 1)) * 100, 8)}%` }}
                      />
                    )}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">
                  {b.month.slice(5)}/{b.month.slice(2, 4)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[10px] text-white/55">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#F0D788]/60 border border-[#D4AF37]/50" /> Leads
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/70 border border-emerald-300" /> Closed
          </span>
        </div>
      </section>

      {/* Recent activity (server-side stats view — kept for backwards compat) */}
      <section
        data-testid="recent-activity-table"
        className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/20 overflow-hidden"
      >
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/5">
          <Activity size={15} className="text-[#F0D788]" />
          <h3 className="font-display text-base">Recent Activity</h3>
          <span className="ml-auto text-[11px] text-white/45">
            Last {(stats.recent_activity || []).length} events
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/55">
              <tr>
                <th className="text-left px-5 py-2.5">Lead</th>
                <th className="text-left px-5 py-2.5">Interest</th>
                <th className="text-left px-5 py-2.5">Status</th>
                <th className="text-left px-5 py-2.5">Created</th>
              </tr>
            </thead>
            <tbody>
              {(stats.recent_activity || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-white/50 py-8 text-xs">
                    No activity yet. Add your first lead to see it here.
                  </td>
                </tr>
              )}
              {(stats.recent_activity || []).map((l) => (
                <tr key={l.id} className="border-t border-white/5">
                  <td className="px-5 py-2.5">
                    <div className="text-white/90">{l.name}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{l.phone}</div>
                  </td>
                  <td className="px-5 py-2.5 text-white/70">{l.interest}</td>
                  <td className="px-5 py-2.5">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                        l.status === "closed"
                          ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                          : l.status === "qualified"
                          ? "bg-purple-500/15 text-purple-200 border border-purple-500/30"
                          : l.status === "contacted"
                          ? "bg-amber-500/15 text-amber-200 border border-amber-500/30"
                          : "bg-blue-500/15 text-blue-200 border border-blue-500/30"
                      }`}
                    >
                      {l.status}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-white/55 whitespace-nowrap text-xs">
                    {new Date(l.created_at).toLocaleDateString("en-IN", {
                      month: "short",
                      day: "numeric",
                      year: "2-digit",
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <button
        onClick={onOpenCalc}
        data-testid="overview-open-calc"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#D4AF37]/35 hover:bg-[#D4AF37]/10 text-[#F0D788] text-sm"
      >
        <Calculator size={14} /> Open Commission Calculator
      </button>
    </div>
  );
}

/* ─── COMMISSION CALCULATOR ─────────────────────────────────────── */

function CommissionCalculatorCard({ partnerRole }) {
  const [cfg, setCfg] = useState(null);
  const [product, setProduct] = useState("personal");
  const [amountStr, setAmountStr] = useState("500000");
  const [breakdown, setBreakdown] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    partnerApi
      .get("/partner/commission/config")
      .then((r) => setCfg(r.data))
      .catch(() => setCfg(null));
  }, []);

  const compute = useCallback(async () => {
    const amount = Number(amountStr) || 0;
    if (amount < 1000) return;
    setBusy(true);
    try {
      const r = await partnerApi.post("/partner/commission/calc", { product, amount });
      setBreakdown(r.data);
    } finally {
      setBusy(false);
    }
  }, [product, amountStr]);

  useEffect(() => {
    const t = setTimeout(compute, 300);
    return () => clearTimeout(t);
  }, [compute]);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  return (
    <section
      data-testid="commission-calculator"
      className="bg-[#0A1F44] rounded-2xl border border-[#D4AF37]/25 overflow-hidden"
    >
      <div className="p-5 border-b border-white/5 flex items-center gap-2">
        <Calculator size={18} className="text-[#F0D788]" />
        <div>
          <h3 className="font-display text-lg">Commission Calculator</h3>
          <div className="text-[11px] text-white/50 mt-0.5">
            Your role · <strong className="text-[#F0D788]">{(partnerRole || "").toUpperCase()}</strong>
            {" "}
            {cfg?.role_base_pct?.[partnerRole] ? `(base ${cfg.role_base_pct[partnerRole]}%)` : ""}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 p-5">
        {/* Inputs */}
        <div className="space-y-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1.5">
              Loan Type
            </div>
            <select
              data-testid="calc-product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm"
            >
              {(cfg?.products || ["personal","home","business","msme","lap","vehicle","gold","education","construction","no-cibil"]).map((p) => (
                <option key={p} value={p}>{p.toUpperCase()}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1.5">
              Loan Amount (₹)
            </div>
            <input
              data-testid="calc-amount"
              type="number"
              min={1000}
              step={10000}
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              placeholder="500000"
              className="w-full px-4 py-3 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37] text-sm tabular-nums"
            />
            <div className="mt-2 flex gap-2 flex-wrap">
              {[100000, 500000, 1000000, 2500000, 5000000].map((v) => (
                <button
                  key={v}
                  onClick={() => setAmountStr(String(v))}
                  className="px-2.5 py-1 rounded-full border border-white/10 hover:border-[#D4AF37]/40 text-[11px] text-white/70 hover:text-[#F0D788]"
                >
                  ₹{(v / 100000).toFixed(0)} L
                </button>
              ))}
            </div>
          </label>
        </div>

        {/* Breakdown */}
        <div
          data-testid="calc-breakdown"
          className="rounded-xl bg-gradient-to-br from-[#04102B] via-[#0a1f44] to-[#04102B] border border-[#D4AF37]/30 p-5"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#F0D788] mb-3">
            Estimated Commission
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-white/70">Base Commission</span>
              <span className="font-semibold tabular-nums">{breakdown ? fmt(breakdown.base) : "—"}</span>
            </div>
            <div className="text-[10px] text-white/45 -mt-1.5">
              @ {breakdown?.base_rate_pct ?? "—"}% of loan amount
            </div>
            <div className="flex items-baseline justify-between pt-1">
              <span className="text-white/70">Volume Bonus</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                {breakdown ? fmt(breakdown.bonus) : "—"}
              </span>
            </div>
            <div className="text-[10px] text-white/45 -mt-1.5">
              {breakdown?.bonus_rate_pct
                ? `+${breakdown.bonus_rate_pct}% (loan ≥ ₹${(cfg?.bonus_min_amount || 500000).toLocaleString("en-IN")})`
                : `Add ₹${((cfg?.bonus_min_amount || 500000) - Number(amountStr || 0)).toLocaleString("en-IN")} more to unlock bonus`}
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-[#D4AF37]/25 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#F0D788]">Total</span>
            <span className="inline-flex items-center gap-0.5 font-display text-2xl md:text-3xl">
              <IndianRupee size={18} className="text-[#F0D788]" />
              <span className="tabular-nums">
                {breakdown ? Number(breakdown.total).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}
              </span>
            </span>
          </div>

          <div className="mt-4 flex items-start gap-1.5 text-[11px] text-white/50 leading-relaxed">
            <Info size={11} className="mt-0.5 text-white/40 shrink-0" />
            <span>
              This is an <strong className="text-white/70">estimate</strong>. Final commission
              depends on loan approval and successful disbursement, and is subject to statutory
              deductions (TDS, GST as applicable).
            </span>
          </div>
        </div>
      </div>
      {busy && (
        <div className="text-[10px] text-white/40 px-5 pb-4 -mt-2">calculating…</div>
      )}
    </section>
  );
}


// ─── Bank Details (Phase 2 · Profile Management) ───────────────────
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCT_RE = /^\d{9,18}$/;
const NAME_RE = /^[A-Za-z][A-Za-z .]{2,79}$/;

function BankDetailsSection({ partner }) {
  const [existing, setExisting] = useState(null); // { has_bank, account_number_masked, ifsc, ... }
  const [registeredName, setRegisteredName] = useState("");
  const [form, setForm] = useState({
    account_holder_name: "",
    account_number: "",
    confirm_account_number: "",
    ifsc: "",
    account_type: "savings",
  });
  const [showAcct, setShowAcct] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null); // { type, msg }
  const [nameSoftWarning, setNameSoftWarning] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await partnerApi.get("/partner/me/bank");
        if (!alive) return;
        setRegisteredName(data.registered_name || partner?.name || "");
        if (data.has_bank) {
          setExisting(data);
          setForm((f) => ({
            ...f,
            account_holder_name: data.account_holder_name || "",
            ifsc: data.ifsc || "",
            account_type: data.account_type || "savings",
          }));
        }
      } catch (_e) {
        // Bank details prefill is best-effort — leave the form blank on failure.
        if (process.env.NODE_ENV === "development") console.debug("bank-details prefill failed:", _e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [partner]);

  const _tokenSet = (a, b) => {
    const norm = (s) => new Set(
      (s || "").toLowerCase().replace(/[^a-z]/g, " ").split(/\s+/).filter((t) => t.length >= 2)
    );
    const ta = norm(a); const tb = norm(b);
    if (!ta.size || !tb.size) return false;
    for (const t of ta) if (tb.has(t)) return true;
    return false;
  };

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
    if (flash) setFlash(null);
  };

  const validate = () => {
    const e = {};
    const name = form.account_holder_name.trim();
    if (name.length < 3) e.account_holder_name = "Please enter your full name (min 3 characters).";
    else if (!NAME_RE.test(name)) e.account_holder_name = "Only letters, spaces and dots are allowed.";

    if (!ACCT_RE.test(form.account_number)) {
      e.account_number = "Account number must be 9–18 digits (numbers only).";
    }
    if (form.account_number && form.confirm_account_number && form.account_number !== form.confirm_account_number) {
      e.confirm_account_number = "Account numbers do not match.";
    }

    const ifsc = form.ifsc.trim().toUpperCase();
    if (!IFSC_RE.test(ifsc)) {
      e.ifsc = "IFSC must look like AAAA0XXXXXX (4 letters, then 0, then 6 alphanumeric).";
    }

    // Soft warning on name mismatch — not an error, but shown inline
    if (!e.account_holder_name && registeredName && !_tokenSet(registeredName, name)) {
      setNameSoftWarning(
        `Heads-up: "${name}" doesn't closely match your registered name "${registeredName}". We'll still save it — please double-check for typos.`
      );
    } else {
      setNameSoftWarning("");
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setFlash(null);
    try {
      const { data } = await partnerApi.put("/partner/me/bank", {
        account_holder_name: form.account_holder_name.trim(),
        account_number: form.account_number,
        confirm_account_number: form.confirm_account_number,
        ifsc: form.ifsc.trim().toUpperCase(),
        account_type: form.account_type || null,
      });
      setExisting(data.bank);
      setForm((f) => ({
        ...f,
        account_number: "",
        confirm_account_number: "",
        account_holder_name: data.bank.account_holder_name || f.account_holder_name,
        ifsc: data.bank.ifsc || f.ifsc,
      }));
      setFlash({
        type: "ok",
        msg: `Bank details ${data.action}. We've notified the admin team.${data.warning ? ` (${data.warning})` : ""}`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail.length && detail[0].msg) {
        setFlash({ type: "err", msg: detail.map((d) => d.msg).join(" · ") });
      } else {
        setFlash({ type: "err", msg: detail || "Could not save bank details. Please retry." });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section data-testid="bank-details-section" className="rounded-2xl border border-[#D4AF37]/20 bg-[#0A1F44] p-8 text-white/60 text-sm">
        <Loader2 className="animate-spin inline-block mr-2" size={14} /> Loading your bank details…
      </section>
    );
  }

  return (
    <section
      data-testid="bank-details-section"
      className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] overflow-hidden"
    >
      <header className="px-6 py-5 border-b border-white/10 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
            Profile · Bank Details
          </div>
          <h2 className="mt-1 font-display text-xl text-white flex items-center gap-2">
            <Landmark size={18} className="text-[#D4AF37]" />
            {existing?.has_bank ? "Update bank details" : "Add bank details for commission payouts"}
          </h2>
          <p className="mt-1 text-xs text-white/55 leading-relaxed max-w-xl">
            Only you can view and change this. Every change is written to the audit log and
            the admin team is notified.
          </p>
        </div>
        {existing?.has_bank && (
          <div data-testid="bank-current-summary" className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-white/40">Currently on file</div>
            <div className="mt-1 font-mono text-sm text-[#F0D788]">{existing.account_number_masked}</div>
            <div className="text-[11px] text-white/55 mt-0.5">IFSC · {existing.ifsc}</div>
          </div>
        )}
      </header>

      <form onSubmit={submit} className="px-6 py-6 space-y-5" data-testid="bank-details-form" noValidate>
        {/* Holder Name */}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="acct-name">
            Account Holder Name <span className="text-[#D4AF37]">*</span>
          </label>
          <input
            id="acct-name"
            data-testid="bank-holder-name"
            type="text"
            value={form.account_holder_name}
            onChange={(e) => setField("account_holder_name", e.target.value)}
            placeholder="As per bank records (letters, spaces, dots only)"
            className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.account_holder_name ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3`}
            autoComplete="off"
          />
          {errors.account_holder_name && (
            <div data-testid="err-holder-name" className="mt-1 text-xs text-rose-300 flex items-center gap-1"><AlertCircle size={11} /> {errors.account_holder_name}</div>
          )}
          {!errors.account_holder_name && nameSoftWarning && (
            <div data-testid="warn-holder-name" className="mt-1 text-xs text-amber-300 flex items-start gap-1.5">
              <AlertCircle size={11} className="mt-0.5" /> {nameSoftWarning}
            </div>
          )}
        </div>

        {/* Account Number + Confirm */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="acct-no">
              Account Number <span className="text-[#D4AF37]">*</span>
            </label>
            <div className="relative">
              <input
                id="acct-no"
                data-testid="bank-account-number"
                type={showAcct ? "text" : "password"}
                inputMode="numeric"
                pattern="\d*"
                value={form.account_number}
                onChange={(e) => setField("account_number", e.target.value.replace(/\D/g, ""))}
                placeholder="9 to 18 digits"
                className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.account_number ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3 pr-10 font-mono tracking-wider`}
                autoComplete="off"
              />
              <button
                type="button"
                data-testid="bank-account-toggle"
                onClick={() => setShowAcct((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
                aria-label={showAcct ? "Hide" : "Show"}
              >
                {showAcct ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {errors.account_number && (
              <div data-testid="err-account-number" className="mt-1 text-xs text-rose-300 flex items-center gap-1"><AlertCircle size={11} /> {errors.account_number}</div>
            )}
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="acct-confirm">
              Confirm Account Number <span className="text-[#D4AF37]">*</span>
            </label>
            <div className="relative">
              <input
                id="acct-confirm"
                data-testid="bank-account-confirm"
                type={showConfirm ? "text" : "password"}
                inputMode="numeric"
                pattern="\d*"
                value={form.confirm_account_number}
                onChange={(e) => setField("confirm_account_number", e.target.value.replace(/\D/g, ""))}
                placeholder="Re-enter to confirm"
                onPaste={(e) => { e.preventDefault(); setFlash({ type: "err", msg: "For safety, please type the confirm number manually." }); }}
                className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.confirm_account_number ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3 pr-10 font-mono tracking-wider`}
                autoComplete="off"
              />
              <button
                type="button"
                data-testid="bank-confirm-toggle"
                onClick={() => setShowConfirm((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
                aria-label={showConfirm ? "Hide" : "Show"}
              >
                {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {errors.confirm_account_number && (
              <div data-testid="err-account-confirm" className="mt-1 text-xs text-rose-300 flex items-center gap-1"><AlertCircle size={11} /> {errors.confirm_account_number}</div>
            )}
          </div>
        </div>

        {/* IFSC + Account Type */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="acct-ifsc">
              IFSC Code <span className="text-[#D4AF37]">*</span>
            </label>
            <input
              id="acct-ifsc"
              data-testid="bank-ifsc"
              type="text"
              value={form.ifsc}
              onChange={(e) => setField("ifsc", e.target.value.toUpperCase().slice(0, 11))}
              placeholder="AAAA0XXXXXX"
              maxLength={11}
              className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.ifsc ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3 font-mono tracking-wider uppercase`}
              autoComplete="off"
            />
            {errors.ifsc && (
              <div data-testid="err-ifsc" className="mt-1 text-xs text-rose-300 flex items-center gap-1"><AlertCircle size={11} /> {errors.ifsc}</div>
            )}
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="acct-type">
              Account Type
            </label>
            <select
              id="acct-type"
              data-testid="bank-account-type"
              value={form.account_type}
              onChange={(e) => setField("account_type", e.target.value)}
              className="mt-1 w-full rounded-lg bg-[#04102B] border border-white/10 focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3"
            >
              <option value="savings" className="bg-[#04102B]">Savings</option>
              <option value="current" className="bg-[#04102B]">Current</option>
              <option value="overdraft" className="bg-[#04102B]">Overdraft</option>
            </select>
          </div>
        </div>

        {/* Reassurance */}
        <div className="flex items-start gap-2 text-[11px] text-white/55 leading-relaxed rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <Lock size={12} className="mt-0.5 text-[#D4AF37] shrink-0" />
          <span>
            Only you can see and change these details. Once saved, we only show the last four digits
            of your account number anywhere — the full number is never sent back to the browser or
            included in any email. Every change is logged for audit.
          </span>
        </div>

        {flash && (
          <div
            data-testid="bank-flash"
            className={`text-xs rounded-lg border p-3 flex items-start gap-2 ${
              flash.type === "ok"
                ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-100"
                : "bg-rose-500/10 border-rose-400/40 text-rose-100"
            }`}
          >
            {flash.type === "ok" ? <CheckCircle2 size={14} className="mt-0.5" /> : <AlertCircle size={14} className="mt-0.5" />}
            <span>{flash.msg}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="submit"
            data-testid="bank-save"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-sm font-semibold shadow-lg shadow-[#D4AF37]/20 transition disabled:opacity-60"
          >
            {saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Save size={13} /> {existing?.has_bank ? "Update Bank Details" : "Save Bank Details"}</>}
          </button>
        </div>
      </form>

      <footer className="px-6 py-4 border-t border-white/10 bg-white/[0.02] text-[11px] text-white/45 flex items-center gap-2">
        <ShieldCheck size={12} className="text-[#D4AF37]" />
        Your bank details are used only for commission payouts.
      </footer>
    </section>
  );
}



// ─── Profile tab wrapper (Photo + Edit Profile + Bank) ─────────────
function ProfileTab({ partner, onPartnerUpdate }) {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await partnerApi.get("/partner/me");
        if (alive) setProfile(data);
      } catch (_e) {
        // Partner /me is best-effort here — the outer route already gated auth.
        if (process.env.NODE_ENV === "development") console.debug("partner /me prefetch failed:", _e);
      }
    })();
    return () => { alive = false; };
  }, []);

  const merged = { ...(partner || {}), ...(profile || {}) };

  return (
    <div data-testid="profile-tab" className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <ProfilePhotoCard
          partner={merged}
          onUpdated={(patch) => {
            setProfile((p) => ({ ...(p || {}), ...patch }));
            onPartnerUpdate?.(patch);
          }}
        />
        <EditProfileCard
          partner={merged}
          onSaved={(patch) => {
            setProfile((p) => ({ ...(p || {}), ...patch }));
            onPartnerUpdate?.(patch);
          }}
        />
      </div>
      <BankDetailsSection partner={merged} />
    </div>
  );
}


// ─── Profile Photo (upload + preview) ──────────────────────────────
const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PHOTO_MAX = 2 * 1024 * 1024;

function ProfilePhotoCard({ partner, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [previewBust, setPreviewBust] = useState(Date.now());

  const photoSrc = partner?.photo_url
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${partner.photo_url}?t=${previewBust}`
    : null;

  const initials = (partner?.name || "P")
    .split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  const onFile = async (e) => {
    setErr(""); setOk("");
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      setErr("Please choose a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > PHOTO_MAX) {
      setErr("Photo must be under 2 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await partnerApi.post("/partner/me/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Server needs the auth header; interceptor handles it.
      });
      setOk("Profile photo updated.");
      setPreviewBust(Date.now());
      onUpdated?.({ photo_url: data.photo_url });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Photo upload failed. Please try another image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="profile-photo-card"
      className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] p-6"
    >
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">Profile Photo</div>
      <h3 className="mt-1 font-display text-lg text-white">How you appear on your dashboard</h3>

      <div className="mt-5 flex items-center gap-5">
        <div className="w-24 h-24 rounded-full bg-[#04102B] border border-[#D4AF37]/40 overflow-hidden grid place-items-center shrink-0 shadow-lg shadow-[#D4AF37]/10">
          {photoSrc ? (
            <img
              data-testid="profile-photo-img"
              src={photoSrc}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={() => setPreviewBust(Date.now() + 1)}
            />
          ) : (
            <span data-testid="profile-photo-initials" className="font-display text-3xl text-[#D4AF37]">
              {initials}
            </span>
          )}
        </div>
        <div className="flex-1">
          <label
            data-testid="profile-photo-choose"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-xs uppercase tracking-wider font-semibold cursor-pointer transition disabled:opacity-60"
          >
            {busy ? <><Loader2 size={13} className="animate-spin" /> Uploading…</> : <><Plus size={13} /> {partner?.photo_url ? "Replace Photo" : "Upload Photo"}</>}
            <input
              data-testid="profile-photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onFile}
              disabled={busy}
              className="hidden"
            />
          </label>
          <div className="mt-2 text-[11px] text-white/50 leading-relaxed max-w-sm">
            JPG, PNG or WEBP · up to 2&nbsp;MB. Your photo is private and only visible to you.
          </div>
          {err && (
            <div data-testid="profile-photo-err" className="mt-2 text-xs text-rose-300 flex items-center gap-1"><AlertCircle size={11} /> {err}</div>
          )}
          {ok && (
            <div data-testid="profile-photo-ok" className="mt-2 text-xs text-emerald-300 flex items-center gap-1"><CheckCircle2 size={11} /> {ok}</div>
          )}
        </div>
      </div>
    </section>
  );
}


// ─── Editable profile details (name, address, city, state) ─────────
function EditProfileCard({ partner, onSaved }) {
  const [form, setForm] = useState({
    name: "", address: "", city: "", state: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    setForm({
      name: partner?.name || "",
      address: partner?.address || "",
      city: partner?.city || "",
      state: partner?.state || "",
    });
  }, [partner?.name, partner?.address, partner?.city, partner?.state]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
    if (flash) setFlash(null);
  };

  const validate = () => {
    const e = {};
    const name = (form.name || "").trim();
    if (name.length < 3) e.name = "Please enter your full name (min 3 characters).";
    else if (!NAME_RE.test(name)) e.name = "Only letters, spaces and dots are allowed in the name.";
    if ((form.address || "").length > 200) e.address = "Address is too long (max 200).";
    if ((form.city || "").length > 80) e.city = "City name is too long (max 80).";
    if ((form.state || "").length > 80) e.state = "State name is too long (max 80).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true); setFlash(null);
    try {
      const body = {
        name: form.name.trim(),
        address: form.address?.trim() || null,
        city: form.city?.trim() || null,
        state: form.state?.trim() || null,
      };
      const { data } = await partnerApi.patch("/partner/me/profile", body);
      setFlash({ type: "ok", msg: "Profile updated." });
      onSaved?.(data.profile || body);
    } catch (e2) {
      setFlash({ type: "err", msg: e2?.response?.data?.detail || "Could not save profile changes." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="edit-profile-card"
      className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] p-6"
    >
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">Profile Details</div>
      <h3 className="mt-1 font-display text-lg text-white">Edit your details</h3>
      <p className="mt-1 text-[11px] text-white/55 max-w-md">
        Update your name spelling, address, city and state. Contact number and email
        are locked for security — reach out to support to change them.
      </p>

      <form onSubmit={submit} className="mt-4 space-y-4" noValidate>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="p-name">Full Name <span className="text-[#D4AF37]">*</span></label>
          <input
            id="p-name"
            data-testid="profile-name"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.name ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3`}
          />
          {errors.name && <div data-testid="err-profile-name" className="mt-1 text-xs text-rose-300 flex items-center gap-1"><AlertCircle size={11} /> {errors.name}</div>}
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="p-addr">Address</label>
          <textarea
            id="p-addr"
            data-testid="profile-address"
            rows={2}
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="House / Flat, Street, Landmark"
            className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.address ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3`}
          />
          {errors.address && <div className="mt-1 text-xs text-rose-300">{errors.address}</div>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="p-city">City</label>
            <input
              id="p-city"
              data-testid="profile-city"
              value={form.city}
              onChange={(e) => setField("city", e.target.value)}
              className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.city ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3`}
            />
            {errors.city && <div className="mt-1 text-xs text-rose-300">{errors.city}</div>}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/55" htmlFor="p-state">State</label>
            <input
              id="p-state"
              data-testid="profile-state"
              value={form.state}
              onChange={(e) => setField("state", e.target.value)}
              className={`mt-1 w-full rounded-lg bg-[#04102B] border ${errors.state ? "border-rose-400/60" : "border-white/10"} focus:border-[#D4AF37] outline-none text-sm text-white/95 p-3`}
            />
            {errors.state && <div className="mt-1 text-xs text-rose-300">{errors.state}</div>}
          </div>
        </div>

        {flash && (
          <div
            data-testid="profile-flash"
            className={`text-xs rounded-lg border p-3 flex items-start gap-2 ${
              flash.type === "ok"
                ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-100"
                : "bg-rose-500/10 border-rose-400/40 text-rose-100"
            }`}
          >
            {flash.type === "ok" ? <CheckCircle2 size={13} className="mt-0.5" /> : <AlertCircle size={13} className="mt-0.5" />}
            <span>{flash.msg}</span>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="profile-save"
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-xs uppercase tracking-wider font-semibold disabled:opacity-60"
          >
            {busy ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Save size={13} /> Save Changes</>}
          </button>
        </div>
      </form>
    </section>
  );
}

